
/**
 * Module dependencies.
 */

import { GetServerSidePropsContext } from 'next';
import { color, media, units } from 'src/styles/utils';
import { eLearningRoutes } from 'src/core/routes';
import { fetchELearningPage } from 'src/api/app/e-learning/fetch-e-learning-page';
import { isProduction } from 'src/core/utils/environment';
import { parseCookies, setCookie } from 'nookies';
import { routeTemplate } from 'src/core/utils/routes';
import { theme } from 'styled-tools';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import { useSnackbar } from 'src/context/snackbar/context';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import Password from 'src/components/core/forms/fields/password';
import RawHtml from 'src/components/core/raw-html';
import React, {
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';

import Type from 'src/components/core/typography';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import useAutoFocus from 'src/hooks/use-auto-focus';

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  background-color: ${color('beige200')};
  height: 193.2vw;
  padding-top: calc(${theme('dimensions.navbarHeightMobile')}px + ${units(8)});

  ${media.min('ms')`
    padding-top: calc(${theme('dimensions.navbarHeightMobile')}px + ${units(14)});
  `}

  ${media.min('lg')`
    height: 63.3vw;
    padding-top: calc(${theme('dimensions.navbarHeight')}px + ${units(23)});
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    '. title .'
    '. lead  .'
    '. form  .';
  grid-template-columns: ${theme('grid.gutterMobile')}px 1fr ${theme('grid.gutterMobile')}px;

  ${media.min('ms')`
    grid-template-areas:
      '. title title . form .'
      '. lead  .     . form .';
    grid-template-columns: 1fr 4fr repeat(2, 1fr) 4fr 1fr;
  `}

  ${media.min('xxl')`
    grid-template-areas:
      '. title . form .'
      '. lead  . form .';
    grid-template-columns: 2fr 4fr 1fr 4fr 2fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  grid-area: title;
  margin-bottom: ${units(1)};

  ${media.min('ms')`
    margin-bottom: ${units(3)};
  `}

  ${media.min('lg')`
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `Lead` styled component.
 */

const Lead = styled(Type.H6).attrs({
  as: 'p',
  small: true
})`
  color: ${color('grey400')};
  grid-area: lead;

  ${media.max('ms')`
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `Form` styled component.
 */

const Form = styled.form`
  grid-area: form;

  ${media.min('lg')`
    padding-top: ${units(6)};
  `}
`;

/**
 * `StyledPassword` styled component.
 */

const StyledPassword = styled(Password)`
  margin-bottom: ${units(2.5)};
`;

/**
 * `ELearningLogin` page.
 */

const ELearningLogin = (): ReactElement => {
  const { locale, translate } = useTranslate();
  const { query } = useRouter();
  const { showMessage } = useSnackbar();
  const router = useRouter();
  const slug = get(query, 'slug');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const inputRef = useRef();
  const savedPasswords = useMemo(() => {
    const cookies = parseCookies();

    return cookies['e-learning'] ? JSON.parse(cookies['e-learning']) : {};
  }, []);

  const {
    errors,
    getValues,
    handleSubmit,
    register
  } = useForm({ mode: 'onSubmit' });

  const onSubmit = useCallback(() => {
    setIsSubmitting(true);

    fetchELearningPage({
      locale,
      password: getValues('password')
    })
      .then(data => {
        if (data.slug === slug) {
          savedPasswords[slug] = Buffer.from(getValues('password')).toString('base64');

          setCookie(null, 'e-learning', JSON.stringify(savedPasswords), {
            maxAge: 15 * 24 * 60 * 60,
            path: '/',
            secure: isProduction()
          });

          showMessage(translate('common:eLearning.messages.success'), {
            appearance: 'success'
          });

          router.push(routeTemplate(eLearningRoutes.eLearning, { slug }));

          return;
        }

        showMessage(translate('common:eLearning.messages.error'), {
          appearance: 'error'
        });
      })
      .catch(() => {
        showMessage(translate('common:eLearning.messages.error'), {
          appearance: 'error'
        });
      })
      .finally(() => setIsSubmitting(false));
  }, [getValues, locale, router, savedPasswords, showMessage, slug, translate]);

  useAutoFocus({
    isActive: true,
    ref: inputRef
  });

  return (
    <Wrapper>
      <Grid>
        <RawHtml element={Title}>
          {translate('common:eLearning.title')}
        </RawHtml>

        <Lead>
          {translate('common:eLearning.lead')}
        </Lead>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <StyledPassword
            disabled={isSubmitting}
            error={errors?.password}
            label={translate('forms:passwordLabel')}
            name={'password'}
            placeholder={translate('forms:passwordPlaceholder')}
            ref={element => {
              // @ts-ignore
              inputRef.current = element;
              register<any>(element, {
                required: {
                  message: translate('forms:requiredField'),
                  value: true
                }
              });
            }}
          />

          <Button
            disabled={isSubmitting}
            isLoading={isSubmitting}
            type={'submit'}
          >
            {translate('common:actions.enter')}
          </Button>
        </Form>
      </Grid>
    </Wrapper>
  );
};

/**
 * Export `getServerSideProps`.
 */

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const cookies = parseCookies(ctx);
  const savedPasswords = cookies['e-learning'] ? JSON.parse(cookies['e-learning']) : {};
  const slug = get(ctx?.query, 'slug');
  const locale = get(ctx, 'locale');

  if (!isEmpty(savedPasswords) && slug in savedPasswords) {
    return await fetchELearningPage({
      locale,
      password: Buffer.from(savedPasswords[slug], 'base64').toString()
    }).then(() => {
      return {
        redirect: {
          destination: routeTemplate(eLearningRoutes.eLearning, { slug }),
          permanent: false
        }
      };
    }).catch(() => {
      return {
        props: {}
      };
    });
  }

  return {
    props: {}
  };
}

/**
 * Export `ELearningLogin` page.
 */

export default ELearningLogin;
